import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { useState } from "react";
import { ATHENS_GRAY } from "@/shared/colors";
import { CenteredLazyLoading } from "../../controls/LazyLoading";
import { MillCalendarCard } from "./calendar/MillCalendarCard";
import { HeatListCard } from "./components/HeatListCard";
import { HoursProgressBar } from "./components/HoursProgressBar";
import { IncentiveTankChart } from "./components/IncentiveTankChart";
import { ProductionChart } from "./components/ProductionChart";
import { ProjectedTimes } from "./components/ProjectedTimes";
import { RollingTotalStats } from "./components/RollingTotalStats";
import { StatusIndicator } from "./components/StatusIndicator";
import { useMillOverviewData } from "./hooks/useMillOverviewData";

// The calendar column defines the shared row height: the parent row fixes it
// and every column fills 100% of it.
const ROW_HEIGHT = "calc(100vh - 130px)";

export const MillOverviewPage = () => {
	const [error, setError] = useState("");

	const { data, isLoading } = useMillOverviewData({
		onError: (e) => setError(e.message),
	});

	return (
		<>
			<HelmetDexteel title="2 Mill Overview" />
			<Box sx={{ p: 1.5, backgroundColor: ATHENS_GRAY, minHeight: "100%" }}>
				<Grid2 container spacing={1} columns={24}>
					<Grid2 size={{ xs: 24 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								2 Mill Overview
							</Typography>
							{data && <StatusIndicator isInDelay={data.IsInDelay} />}
						</Box>
					</Grid2>

					{isLoading && (
						<Grid2 size={{ xs: 24 }}>
							<CenteredLazyLoading />
						</Grid2>
					)}

					{data && (
						<Grid2
							size={{ xs: 24, lg: 7, xl: 5 }}
							sx={{ height: { lg: ROW_HEIGHT } }}
						>
							<Box
								sx={{
									display: "grid",
									gridTemplateRows: "auto auto minmax(0, 1fr) auto",
									gap: 1,
									height: { lg: "100%" },
									overflowY: { lg: "auto" },
									pb: 0.5,
									px: 0.25,
								}}
							>
								<ProductionChart data={data.ProductionChart} />
								<IncentiveTankChart data={data.IncentiveTank} />

								<Paper
									elevation={1}
									sx={{
										p: 1,
										minWidth: 0,
										minHeight: 0,
										overflow: "visible",
										display: "flex",
										flexDirection: "column",
									}}
								>
									<HoursProgressBar
										eventStart={data.CurrentSection.EventStart}
										scheduledEnd={data.CurrentSection.ScheduledEnd}
										projectedEnd={data.CurrentSection.ProjectedRollOut}
										scheduledHours={data.CurrentSection.ScheduledHours}
										currentHours={data.CurrentSection.CurrentHours}
										remainingHours={data.CurrentSection.RemainingHours}
										varianceHours={data.CurrentSection.VarianceHours}
									/>
									<ProjectedTimes
										projectedRollOut={data.CurrentSection.ProjectedRollOut}
										projectedBreakin={data.UpcomingSection.ProjectedBreakin}
									/>
								</Paper>

								<RollingTotalStats data={data.RollingTotal} />
							</Box>
						</Grid2>
					)}

					<Grid2
						size={{ xs: 24, lg: 17, xl: 10 }}
						sx={{ height: { lg: ROW_HEIGHT } }}
					>
						<HeatListCard onError={(e) => setError(e.message)} />
					</Grid2>

					<Grid2 size={{ xs: 24, xl: 9 }} sx={{ height: { lg: ROW_HEIGHT } }}>
						<MillCalendarCard onError={(e) => setError(e.message)} />
					</Grid2>
				</Grid2>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
